/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  IConceptoVerificacion,
  TipoCampo,
} from "src/utils/aspirantes/verificaciones";
import { Box, Button, Typography } from "@mui/material";
import React, { useState } from "react";
import { OpcionSelect } from "src/components/input/TextInput";
import {
  opcionesSexo,
  opcionesTipoIdentificacionPersonaNatural,
} from "src/utils/constantes";
import {
  useObtenerDepartamentosQuery,
  useObtenerHorariosDisponibilidadQuery,
  useObtenerMunicipiosQuery,
  useObtenerPerfilesQuery,
  useObtenerSedesQuery,
} from "src/redux/api/maestrosApi";
import {
  departamentosRespToOpcionSelect,
  horariosRespToOpcionSelect,
  municipiosRespToOpcionSelect,
  perfilesRespToOpcionSelect,
  sedeRespToOpcionSelect,
} from "src/redux/api/types/maestros";
import CustomDialog from "src/components/dialogo/CustomDialog";
import VisorPdf from "src/components/visor-pdf/VisorPdf";

export type InformacionAspirantePros = {
  props: IConceptoVerificacion;
  aspirante: any;
};

export type VisualizadorDeSelectPros = {
  label: string;
  valor: any | any[]; // Soporta valor único o múltiple
  opciones: OpcionSelect[];
};

const VisualizadorDeSelect = ({
  label,
  valor,
  opciones,
}: VisualizadorDeSelectPros) => {
  const valores = Array.isArray(valor) ? valor : [valor.toString()]; // Asegura que siempre sea un array

  const opcionesFiltradas = opciones.filter((opcion) =>
    valores.includes(opcion.valor.toString()),
  ); // Filtra las opciones que coincidan con los valores

  return (
    <Box py={2}>
      <Typography fontWeight="bold">{label}</Typography>
      {opcionesFiltradas.length === 1 ? (
        // Mostrar valor único como texto
        <Typography color="textSecondary">
          {opcionesFiltradas[0].label}
        </Typography>
      ) : (
        // Mostrar valores múltiples como lista
        <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
          {opcionesFiltradas.map((opcion) => (
            <li key={opcion.valor}>
              <Typography color="textSecondary">{opcion.label}</Typography>
            </li>
          ))}
        </ul>
      )}
    </Box>
  );
};

export type VisualizadorDeAdjuntoPros = {
  label: string;
  valor: any;
  valorFecha?: string;
};

const VisualizadorDeAdjunto = ({
  label,
  valor,
  valorFecha,
}: VisualizadorDeAdjuntoPros) => {
  const [mostrarVisorPdf, setMostrarVisorPdf] = useState(false);
  const [soporteAvisualizar, setSoporteAvisualizar] = useState(undefined);
  const handleDownload = () => {
    setSoporteAvisualizar(valor);
    setMostrarVisorPdf(true);
  };
  return (
    <Box py={2}>
      <Typography fontWeight="bold">{label}</Typography>
      {valor ? (
        <Box>
          <Typography variant="body2" color="textSecondary">
            Archivo cargado, haga clic{" "}
            <Button
              onClick={handleDownload}
              sx={{ textTransform: "capitalize", px: 1, minWidth: 0 }}
            >
              Aquí
            </Button>
            para visualizar.
          </Typography>
          {valorFecha && (
            <Typography>
              Fecha de generación del certificado: {valorFecha}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ marginTop: "8px" }}
        >
          Ningún archivo cargado
        </Typography>
      )}
      <CustomDialog
        mostrar={mostrarVisorPdf}
        onClose={() => {
          setSoporteAvisualizar(undefined);
          setMostrarVisorPdf(false);
        }}
        contenido={<VisorPdf pdfFile={soporteAvisualizar} />}
        acciones={
          <Button
            onClick={() => {
              setMostrarVisorPdf(false);
              setSoporteAvisualizar(undefined);
            }}
          >
            Cerrar
          </Button>
        }
        titulo={`Soporte `}
        tamanioMaximo="md"
        fullWidth
      />
    </Box>
  );
};

const InformacionAspirante = ({
  props,
  aspirante,
}: InformacionAspirantePros) => {
  const [mostrarVisorPdf, setMostrarVisorPdf] = useState(false);
  const [soporteAvisualizar, setSoporteAvisualizar] = useState(undefined);
  const handleDownload = (valor: any) => {
    setSoporteAvisualizar(valor);
    setMostrarVisorPdf(true);
  };
  const { data: departamentosResp } = useObtenerDepartamentosQuery();
  const { data: perfilesResp } = useObtenerPerfilesQuery();
  const { data: horariosResp } = useObtenerHorariosDisponibilidadQuery();
  const { data: sedesResp } = useObtenerSedesQuery();
  const { data: municipiosResp } = useObtenerMunicipiosQuery(
    aspirante.departamentoResidenciaId,
    { skip: aspirante.departamentoResidenciaId === "" },
  );
  switch (props.tipo) {
    case TipoCampo.TEXTO:
      return (
        <Box py={2}>
          <Typography fontWeight="bold">{props.label}</Typography>
          <Typography color="textSecondary">
            {aspirante[props.campo]}
          </Typography>
        </Box>
      );
    case TipoCampo.FORMACION_ACADEMICA:
      return (
        <Box py={2}>
          <Typography fontWeight="bold">{props.label}</Typography>
          <Box pl={2}>
            <Typography color="textSecondary">
              <b>Titulo obtenido:</b>{" "}
              {aspirante[props.campo][0].nombreTituloObtenido}
            </Typography>
            <Typography color="textSecondary">
              <b>Institución:</b> {aspirante[props.campo][0].nombreInstitucion}
            </Typography>
            <Typography color="textSecondary">
              <b>Fecha de graduación: </b>{" "}
              {aspirante[props.campo][0].fechaGraduacion}
            </Typography>
          </Box>
        </Box>
      );
    case TipoCampo.EXPERIENCIA_LABORAL:
      return (
        <Box py={2}>
          <Typography fontWeight="bold">{props.label}</Typography>
          {aspirante[props.campo].map((experiencia: any, index: any) => {
            return (
              <Box display="flex" flexDirection="column" pl={2} key={experiencia.experienciaId}>
                <Typography fontWeight="bold">
                  Experiencia {index + 1}:
                </Typography>
                <Box display="flex" flexDirection="column" pl={2}>
                  <Typography color="textSecondary">
                    <b>Empresa o institucion:</b>{" "}
                    {experiencia.nombreEmpresaInstitucion}
                  </Typography>
                  <Typography color="textSecondary">
                    <b>Tipo de experiencia:</b> {experiencia.tipo}
                  </Typography>
                  <Typography color="textSecondary">
                    <b>Fechas:</b> De {experiencia.fechaInicio} a{" "}
                    {experiencia.fechaFin}
                  </Typography>
                  <Typography color="textSecondary">
                    <b>Resumen de funciones:</b> {experiencia.resumenFunciones}
                  </Typography>
                  <Typography color="textSecondary">
                    <b>Jefe inmediato:</b> {experiencia.nombreJefeInmediato}
                  </Typography>
                  <Typography color="textSecondary">
                    <b>Teléfono de jefe inmediato</b>:{" "}
                    {experiencia.telefonoJefeInmediato}
                  </Typography>
                  <Typography color="textSecondary">
                    <b>Certificado</b>:{" "}
                    {(experiencia.soporteId === "" ||
                      !experiencia.soporteId) && (
                      <>Ningún archivo seleccionado</>
                    )}
                    {experiencia.soporteId && experiencia.soporteId !== "" && (
                      <Typography variant="body2" color="textSecondary">
                      Archivo cargado, haga clic{" "}
                      <Button
                        onClick={()=> {handleDownload(experiencia.soporteId)}}
                        sx={{ textTransform: "capitalize", px: 1, minWidth: 0 }}
                      >
                        Aquí
                      </Button>
                      para visualizar.
                    </Typography>
                    )}
                  </Typography>
                </Box>
                <CustomDialog
                  mostrar={mostrarVisorPdf}
                  onClose={() => {
                    setSoporteAvisualizar(undefined);
                    setMostrarVisorPdf(false);
                  }}
                  contenido={<VisorPdf pdfFile={soporteAvisualizar} />}
                  acciones={
                    <Button
                      onClick={() => {
                        setMostrarVisorPdf(false);
                        setSoporteAvisualizar(undefined);
                      }}
                    >
                      Cerrar
                    </Button>
                  }
                  titulo={`Soporte `}
                  tamanioMaximo="md"
                  fullWidth
                />
              </Box>
            );
          })}
        </Box>
      );
    case TipoCampo.FECHA:
      return (
        <Box py={2}>
          <Typography fontWeight="bold">{props.label}</Typography>
          {aspirante[props.campo]}
        </Box>
      );
    case TipoCampo.SELECT_TIPO_DOC:
      return (
        <VisualizadorDeSelect
          label={props.label}
          valor={aspirante[props.campo]}
          opciones={opcionesTipoIdentificacionPersonaNatural}
        />
      );
    case TipoCampo.SELECT_PERFIL:
      return (
        <VisualizadorDeSelect
          label={props.label}
          valor={aspirante[props.campo]}
          opciones={
            perfilesResp ? perfilesRespToOpcionSelect(perfilesResp) : []
          }
        />
      );
    case TipoCampo.SELECT_DPTO:
      return (
        <VisualizadorDeSelect
          label={props.label}
          valor={aspirante[props.campo]}
          opciones={
            departamentosResp
              ? departamentosRespToOpcionSelect(departamentosResp)
              : []
          }
        />
      );
    case TipoCampo.SELECT_MPIO:
      return (
        <VisualizadorDeSelect
          label={props.label}
          valor={aspirante[props.campo]}
          opciones={
            municipiosResp ? municipiosRespToOpcionSelect(municipiosResp) : []
          }
        />
      );
    case TipoCampo.SELECT_SEXO:
      return (
        <VisualizadorDeSelect
          label={props.label}
          valor={aspirante[props.campo]}
          opciones={opcionesSexo}
        />
      );
    case TipoCampo.HORARIOS:
      return (
        <VisualizadorDeSelect
          label={props.label}
          valor={aspirante[props.campo]}
          opciones={
            horariosResp ? horariosRespToOpcionSelect(horariosResp) : []
          }
        />
      );
    case TipoCampo.SEDES_DISPONIBLES:
      return (
        <VisualizadorDeSelect
          label={props.label}
          valor={aspirante[props.campo]}
          opciones={sedesResp ? sedeRespToOpcionSelect(sedesResp) : []}
        />
      );
    case TipoCampo.ADJUNTO:
      return (
        <VisualizadorDeAdjunto
          label={props.label}
          valor={aspirante[props.campo]}
          valorFecha={
            props.campoFecha ? aspirante[props.campoFecha] : undefined
          }
        />
      );
    default:
      return <Box>No soportado</Box>;
  }
};

export default InformacionAspirante;
